import { Box } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Document } from 'react-pdf';
import type { OnDocumentLoadSuccess } from 'react-pdf/dist/esm/shared/types.js';

import PdfPreview, { PdfGridView } from '@components/PdfPreview';

import { useGetS3AssetRecord } from 'generated/file.generated';

import { usePageOrdering } from '../hooks/usePageOrdering';
import { PdfEmptyState } from './PdfEmptyState';
import { PdfLoadingState } from './PdfLoadingState';

const messages = defineMessages({
  loading: {
    id: 'autoCuration.pdfSplitEditor.loading',
    defaultMessage: 'Loading, please wait...',
  },
});

interface DocumentPreviewProps {
  patientId?: string | null;
  binaryId?: string | null;
  viewMode: 'grid' | 'standard';
  pageGroups: number[][];
  selectedPage?: number;
  totalPages: number;
  removedPages: number[];
  onLoadSuccess: OnDocumentLoadSuccess;
  onPageClick: (page: number) => void;
  onSelectionChange: (selectedPages: number[]) => void;
  onRemovePage: (pageNum: number) => void;
  onChangeCurrentPage: (pageNum: number) => void;
  onReorderPages: (newGroups: number[][]) => void;
}
export const DocumentPreview = ({
  binaryId,
  patientId,
  viewMode,
  pageGroups,
  totalPages,
  selectedPage,
  removedPages,
  onReorderPages,
  onChangeCurrentPage,
  onLoadSuccess,
  onPageClick,
  onSelectionChange,
  onRemovePage,
}: DocumentPreviewProps) => {
  const { formatMessage } = useIntl();
  const { data, loading } = useGetS3AssetRecord({
    variables: {
      input: {
        patient_id: patientId ?? '',
        record_id: binaryId ?? '',
      },
    },
    skip: !patientId || !binaryId,
    fetchPolicy: 'network-only',
  });
  const pdfUrl = data?.getS3AssetRecord.presignedGetUrl;

  const orderedPages = usePageOrdering({
    numPages: totalPages,
    pageGroups,
    removedPages,
  });

  if (loading) {
    return <PdfLoadingState height="100%" width="100%" />;
  } else if (!pdfUrl) {
    <PdfEmptyState height="100%" width="100%" />;
  }

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        overflow: 'auto',
        border: 1,
        borderColor: 'grey.300',
        borderRadius: 1,
        bgcolor: 'grey.50',
        p: 1.5,
        '& > .react-pdf-document': {
          height: '100%',
        },
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          bgcolor: 'grey.100',
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'grey.400',
          borderRadius: '4px',
          '&:hover': {
            bgcolor: 'grey.600',
          },
        },
        '& .react-pdf__Page__canvas': {
          margin: '0 auto',
        },
      }}
    >
      <Document
        file={pdfUrl}
        onLoadSuccess={onLoadSuccess}
        onLoadError={(_error) => {
          // Error handled by usePdfPreviewUrl hook
        }}
        loading={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
            }}
          >
            {formatMessage(messages.loading)}
          </Box>
        }
        className="react-pdf-document"
      >
        {viewMode === 'standard' ? (
          <PdfPreview
            selectedPage={selectedPage}
            onPageChange={onChangeCurrentPage}
            pages={orderedPages}
          />
        ) : (
          <PdfGridView
            pages={orderedPages}
            onPageClick={onPageClick}
            currentPage={selectedPage}
            pageGroups={pageGroups}
            onReorderPages={onReorderPages}
            onSelectionChange={onSelectionChange}
            onRemovePage={onRemovePage}
          />
        )}
      </Document>
    </Box>
  );
};
